import { differenceInSeconds, format, isThisYear } from 'date-fns'
import { enUS, ja as jaLocale, zhCN } from 'date-fns/locale'
import type { Locale as DateFnsLocale } from 'date-fns'
import { useMemo } from 'react'

import { useI18n } from '@/i18n'
import type { Locale } from '@/i18n'

/**
 * 时间与计数的本地化。
 *
 * 这几个词（「刚刚」「秒」「万」）**刻意不放进 i18n 词典**：它们和数字是
 * 拼在一起的紧凑格式，中文 `3小时`、英文 `3h`、日文 `3時間` 连空格的有无
 * 都不一样，翻译成完整词（"3 hours"）会把时间线的布局撑坏。
 * 放在这里，改动的人能同时看到拼接逻辑。
 */
interface Units {
  dateFns: DateFnsLocale
  justNow: string
  /** `${n}${sec}` 这样直接拼，所以空格要写进来 */
  sec: string
  min: string
  hour: string
  /** 今年内的日期 / 跨年的日期 */
  dayPattern: string
  fullDatePattern: string
  timePattern: string
  joined: (month: string) => string
  /** 计数缩写的量级：[阈值, 除数, 后缀] 从小到大 */
  scales: [number, number, string][]
}

const UNITS: Record<Locale, Units> = {
  zh: {
    dateFns: zhCN,
    justNow: '刚刚',
    sec: '秒',
    min: '分钟',
    hour: '小时',
    dayPattern: 'M月d日',
    fullDatePattern: 'yyyy年M月d日',
    timePattern: 'yyyy年M月d日 HH:mm',
    joined: (month) => `${month}加入`,
    scales: [
      [10_000, 10_000, '万'],
      [100_000_000, 100_000_000, '亿'],
    ],
  },
  en: {
    dateFns: enUS,
    justNow: 'now',
    sec: 's',
    min: 'm',
    hour: 'h',
    dayPattern: 'MMM d',
    fullDatePattern: 'MMM d, yyyy',
    timePattern: "MMM d, yyyy 'at' HH:mm",
    joined: (month) => `Joined ${month}`,
    scales: [
      [1_000, 1_000, 'K'],
      [1_000_000, 1_000_000, 'M'],
      [1_000_000_000, 1_000_000_000, 'B'],
    ],
  },
  ja: {
    dateFns: jaLocale,
    justNow: 'たった今',
    sec: '秒',
    min: '分',
    hour: '時間',
    dayPattern: 'M月d日',
    fullDatePattern: 'yyyy年M月d日',
    timePattern: 'yyyy年M月d日 HH:mm',
    joined: (month) => `${month}から利用`,
    scales: [
      [10_000, 10_000, '万'],
      [100_000_000, 100_000_000, '億'],
    ],
  },
}

/** 时间线上的相对时间：<1m / 5m / 3h / 9月20日 / 2025年9月20日 */
export function relativeTime(iso: string, now: Date = new Date(), locale: Locale = 'zh'): string {
  const u = UNITS[locale]
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const seconds = differenceInSeconds(now, date)
  if (seconds < 0) return u.justNow
  if (seconds < 60) return `${seconds}${u.sec}`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}${u.min}`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}${u.hour}`
  const pattern = isThisYear(date) ? u.dayPattern : u.fullDatePattern
  return format(date, pattern, { locale: u.dateFns })
}

/** 帖子详情页的完整时间 */
export function absoluteTime(iso: string, locale: Locale = 'zh'): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return format(date, UNITS[locale].timePattern, { locale: UNITS[locale].dateFns })
}

/** 「2020年6月加入」/「Joined June 2020」 */
export function joinedAt(iso: string, locale: Locale = 'zh'): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const u = UNITS[locale]
  const month = format(date, locale === 'en' ? 'MMMM yyyy' : 'yyyy年M月', { locale: u.dateFns })
  return u.joined(month)
}

/** 计数缩写：999 / 1.2万 / 1.5亿；英文是 1.2K / 1.5M */
export function compactCount(n: number, locale: Locale = 'zh'): string {
  if (!Number.isFinite(n) || n < 0) return '0'
  const scales = UNITS[locale].scales
  // 从大到小找第一个够得上的量级
  for (let i = scales.length - 1; i >= 0; i -= 1) {
    const [threshold, divisor, suffix] = scales[i]
    if (n >= threshold) {
      const value = n / divisor
      const shown = value < 10 ? value.toFixed(1).replace(/\.0$/, '') : String(Math.floor(value))
      return `${shown}${suffix}`
    }
  }
  return String(n)
}

/** 组件里用这个：自动绑上当前语言，不用每处都传 */
export function useFormat() {
  const { locale } = useI18n()
  return useMemo(
    () => ({
      relativeTime: (iso: string, now?: Date) => relativeTime(iso, now ?? new Date(), locale),
      absoluteTime: (iso: string) => absoluteTime(iso, locale),
      joinedAt: (iso: string) => joinedAt(iso, locale),
      compactCount: (n: number) => compactCount(n, locale),
    }),
    [locale],
  )
}

/** 视频时长：0:07、1:32。和语言无关，所以不走 useFormat()（D64 同理：数字格式不进词典） */
export function formatDuration(ms: number | null | undefined): string {
  const total = Math.max(0, Math.round((ms ?? 0) / 1000))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}
